import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { observer } from '@legendapp/state/react';
import { useRouter } from 'expo-router';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import theme from '@/config/theme';
import { globalState, CompanyProfile } from '@/state/globalState';
import {
  generateManualExternalId,
  getAvailablePlans,
  registerManualPayment,
  resolveBillingProvider,
  resolvePaymentMethod,
  upsertSubscriptionAdmin,
} from '@/services/subscriptionService';
import { supabase } from '@/services/supabase';
import { useWebAlert } from '@/components/WebAlert';
import { hasDeveloperBypass } from '@/utils/subscriptionAccess';
import { getLogger } from '@/utils/logger';
import { SubscriptionInfo } from '@/types/subscription';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { APP_CONSTANTS } from '@/config/constants';
import {
  getSettingsContentPaddingBottom,
  getSettingsHeaderPaddingTop,
} from '@/utils/safeArea';

type BillingProviderOption = 'manual' | 'stripe' | 'apple';

const logger = getLogger('SETTINGS-DEVELOPER');
const isWeb = Platform.OS === 'web';

const getBillingProviderLabel = (provider?: string): string => {
  if (provider === 'apple') return 'App Store';
  if (provider === 'manual') return 'PIX manual';
  return 'Stripe';
};

let WebDatePicker: any = null;
let datePickerLocale: any = null;
  if (isWeb) {
    try {
      const datePickerModule = require('react-datepicker');
      WebDatePicker = datePickerModule?.default ?? datePickerModule;
      require('react-datepicker/dist/react-datepicker.css');
    datePickerLocale = require('date-fns/locale').ptBR;
  } catch (error) {
    logger.error('[DEV-PANEL] Falha ao carregar react-datepicker', error);
  }
}

let DateTimePickerComponent: any = null;
let DateTimePickerAndroidModule: any = null;
if (!isWeb) {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const dateTimeModule = require('@react-native-community/datetimepicker');
  DateTimePickerComponent = dateTimeModule.default;
  DateTimePickerAndroidModule = dateTimeModule.DateTimePickerAndroid;
}

interface PlanOption {
  id: string;
  name: string;
  description?: string | null;
  price?: number | null;
}

interface CompanyCardProps {
  company: CompanyProfile;
  selected: boolean;
  onPress: () => void;
}

const CompanyCard = ({ company, selected, onPress }: CompanyCardProps) => (
  <TouchableOpacity
    style={[styles.companyCard, selected && styles.companyCardSelected]}
    onPress={onPress}
    activeOpacity={0.85}
  >
    <View style={styles.companyCardHeader}>
      <Text style={styles.companyCardTitle}>{company.nome || 'Empresa sem nome'}</Text>
      {selected && (
        <MaterialCommunityIcons
          name="check-circle"
          size={18}
          color={theme.colors.util.success}
        />
      )}
    </View>
    <Text style={styles.companyCardSubtitle}>
      Plano atual: {company.plano ? company.plano.toUpperCase() : 'não definido'}
    </Text>
    {company.stripe_customer_id ? (
      <Text style={styles.companyCardInfo}>Stripe customer: {company.stripe_customer_id}</Text>
    ) : (
      <Text style={styles.companyCardInfoMuted}>Stripe customer não configurado</Text>
    )}
  </TouchableOpacity>
);

interface DateSelectorProps {
  label: string;
  value: string | null;
  onChange: (iso: string | null) => void;
  allowClear?: boolean;
}

const formatDateForDisplay = (value?: string | null) => {
  if (!value) return 'Selecionar data e hora...';
  try {
    const date = new Date(value);
    return format(date, 'dd/MM/yyyy HH:mm', { locale: ptBR });
  } catch {
    return value;
  }
};

const ensureDatepickerStyles = () => {
  if (!isWeb) return;
  if (document.getElementById('dev-panel-datepicker-css')) return;
  const style = document.createElement('style');
  style.id = 'dev-panel-datepicker-css';
  style.innerHTML = `
    .react-datepicker {
      border: none;
      border-radius: ${theme.borderRadius.md}px;
      background: ${theme.colors.background.primary};
      box-shadow: 0 2px 10px rgba(0,0,0,.08);
      font-family: ${theme.fontFamily?.regular || 'Inter, sans-serif'};
    }
    .dev-panel-datepicker-popper {
      z-index: 9999;
    }
    .react-datepicker__header {
      background: ${theme.colors.background.primary};
      border-bottom: 1px solid ${theme.colors.border.light};
    }
    .react-datepicker__day {
      color: ${theme.colors.text.primary};
    }
    .react-datepicker__day--selected,
    .react-datepicker__day--keyboard-selected {
      background: ${theme.colors.primary.default};
      color: #fff;
    }
  `;
  document.head.appendChild(style);
};

const DateSelector = ({ label, value, onChange, allowClear = false }: DateSelectorProps) => {
  const [iosModalVisible, setIosModalVisible] = useState(false);
  const [iosDate, setIosDate] = useState<Date>(value ? new Date(value) : new Date());

  useEffect(() => {
    if (value) {
      setIosDate(new Date(value));
    }
  }, [value]);

  useEffect(() => {
    ensureDatepickerStyles();
  }, []);

  const openAndroidPicker = () => {
    if (!DateTimePickerAndroidModule) return;
    const initialDate = value ? new Date(value) : new Date();
    let pickedDate: Date | null = null;

    DateTimePickerAndroidModule.open({
      value: initialDate,
      mode: 'date',
      is24Hour: true,
      onChange: (_event: unknown, date?: Date) => {
        if (!date) return;
        pickedDate = date;
        DateTimePickerAndroidModule.open({
          value: pickedDate,
          mode: 'time',
          is24Hour: true,
          onChange: (_eventTime: unknown, timeDate?: Date) => {
            if (!timeDate || !pickedDate) return;
            const finalDate = new Date(pickedDate);
            finalDate.setHours(timeDate.getHours(), timeDate.getMinutes(), 0, 0);
            onChange(finalDate.toISOString());
          },
        });
      },
    });
  };

  const openIOSPicker = () => {
    const initialDate = value ? new Date(value) : new Date();
    setIosDate(initialDate);
    setIosModalVisible(true);
  };

  const applyIOSSelection = () => {
    onChange(iosDate.toISOString());
    setIosModalVisible(false);
  };

  if (isWeb && WebDatePicker) {
    return (
      <View style={styles.dateField}>
        <Text style={styles.label}>{label}</Text>
        <WebDatePicker
          selected={value ? new Date(value) : null}
          onChange={(date: Date | null) => {
            if (!date) {
              if (allowClear) onChange(null);
              return;
            }
            onChange(date.toISOString());
          }}
          showTimeSelect
          showTimeInput
          timeIntervals={15}
          timeFormat="HH:mm"
          dateFormat="dd/MM/yyyy HH:mm"
          locale={datePickerLocale}
          placeholderText="Selecionar..."
          popperClassName="dev-panel-datepicker-popper"
          popperPlacement="bottom-start"
          shouldCloseOnSelect={false}
          withPortal
          portalId="dev-panel-datepicker-portal"
          className="react-datepicker-ignore-onclickoutside"
        />
        {allowClear && value && (
          <TouchableOpacity
            style={styles.clearButton}
            onPress={() => onChange(null)}
          >
            <MaterialCommunityIcons
              name="close-circle-outline"
              size={18}
              color={theme.colors.text.secondary}
            />
            <Text style={styles.clearButtonText}>Limpar</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  }

  return (
    <View style={styles.dateField}>
      <Text style={styles.label}>{label}</Text>
      <TouchableOpacity
        style={styles.dateButton}
        onPress={Platform.OS === 'android' ? openAndroidPicker : openIOSPicker}
        activeOpacity={0.85}
      >
        <Text style={styles.dateButtonText}>{formatDateForDisplay(value)}</Text>
        <MaterialCommunityIcons
          name="calendar"
          size={20}
          color={theme.colors.primary.default}
        />
      </TouchableOpacity>
      {allowClear && value && (
        <TouchableOpacity
          style={styles.clearButtonInline}
          onPress={() => onChange(null)}
        >
          <MaterialCommunityIcons
            name="close-circle-outline"
            size={18}
            color={theme.colors.text.secondary}
          />
          <Text style={styles.clearButtonText}>Limpar</Text>
        </TouchableOpacity>
      )}

      {Platform.OS === 'ios' && DateTimePickerComponent && (
        <Modal
          visible={iosModalVisible}
          transparent
          animationType="fade"
          onRequestClose={() => setIosModalVisible(false)}
        >
          <View style={styles.iosModalOverlay}>
            <View style={styles.iosModalContent}>
              <DateTimePickerComponent
                value={iosDate}
                mode="datetime"
                display="spinner"
                onChange={(_event: unknown, date?: Date) => date && setIosDate(date)}
                locale="pt-BR"
              />
              <View style={styles.iosModalButtons}>
                <TouchableOpacity
                  style={styles.cancelButton}
                  onPress={() => setIosModalVisible(false)}
                >
                  <Text style={styles.cancelButtonText}>Cancelar</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.confirmButton}
                  onPress={applyIOSSelection}
                >
                  <Text style={styles.confirmButtonText}>Confirmar</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>
      )}
    </View>
  );
};

const DeveloperPanelScreen = observer(() => {
  const router = useRouter();
  const safeAreaInsets = useSafeAreaInsets();
  const headerPaddingTop = getSettingsHeaderPaddingTop(safeAreaInsets.top);
  const scrollViewBottomPadding = getSettingsContentPaddingBottom(safeAreaInsets.bottom);
  const { showAlert } = useWebAlert();
  const userProfile = globalState.user.profile.get();
  const isDeveloper = hasDeveloperBypass(userProfile?.user_type);

  const [plans, setPlans] = useState<PlanOption[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [plansError, setPlansError] = useState<string | null>(null);

  const [companies, setCompanies] = useState<CompanyProfile[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companiesError, setCompaniesError] = useState<string | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<CompanyProfile | null>(null);

  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);
  const [billingProvider, setBillingProvider] = useState<BillingProviderOption>('manual');
  const [manualExternalId, setManualExternalId] = useState<string>('');
  const [stripeExternalId, setStripeExternalId] = useState<string>('');
  const [subscriptionPlanId, setSubscriptionPlanId] = useState<string>('');
  const [subscriptionStatus, setSubscriptionStatus] = useState<'ativa' | 'cancelamento_agendado' | 'cancelada' | 'free'>('ativa');
  const [startDate, setStartDate] = useState<string | null>(null);
  const [nextBillingDate, setNextBillingDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [isLoadingContext, setIsLoadingContext] = useState(false);
  const [isSavingSubscription, setIsSavingSubscription] = useState(false);
  const [subscriptionFeedback, setSubscriptionFeedback] = useState<string | null>(null);

  const [paymentAmount, setPaymentAmount] = useState<string>('');
  const [paymentDate, setPaymentDate] = useState<string | null>(null);
  const [paymentReference, setPaymentReference] = useState<string>('');
  const [paymentStatus, setPaymentStatus] = useState<'paid' | 'pending' | 'failed'>('paid');
  const [isRegisteringPayment, setIsRegisteringPayment] = useState(false);
  const [paymentFeedback, setPaymentFeedback] = useState<string | null>(null);

  useEffect(() => {
    if (!isDeveloper) {
      const message = 'Acesso restrito: somente usuários com perfil de desenvolvedor podem abrir este painel.';
      if (Platform.OS !== 'android') {
        showAlert('Acesso negado', message, [{ text: 'Voltar', onPress: () => router.back() }]);
      } else {
        Alert.alert('Acesso negado', message, [{ text: 'OK', onPress: () => router.back() }]);
      }
    }
  }, [isDeveloper, router, showAlert]);

  const loadPlans = useCallback(async () => {
    setIsLoadingPlans(true);
    setPlansError(null);
    try {
      const { data, error } = await getAvailablePlans();
      if (error) {
        const errorMessage = error && typeof error === 'object' && 'message' in error
          ? String((error as { message?: unknown }).message ?? '')
          : '';
        setPlansError(errorMessage || 'Não foi possível carregar os planos.');
      } else if (data) {
        setPlans(data as PlanOption[]);
      }
    } catch (err) {
      setPlansError(err instanceof Error ? err.message : 'Erro inesperado ao carregar planos.');
    } finally {
      setIsLoadingPlans(false);
    }
  }, []);

  const loadCompanies = useCallback(async () => {
    setCompaniesLoading(true);
    setCompaniesError(null);
    try {
      const { data, error } = await supabase
        .from(APP_CONSTANTS.DATABASE.TABLES.EMPRESAS.NAME)
        .select('id, nome, plano, stripe_customer_id, created_at, updated_at')
        .order('nome', { ascending: true });

      if (error) throw error;

      const list = (data || []) as CompanyProfile[];
      setCompanies(list);
      if (list.length > 0 && !selectedCompanyId) {
        setSelectedCompanyId(list[0].id);
        setSelectedCompany(list[0]);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar empresas.';
      logger.error('[DEV-PANEL] Falha ao buscar empresas', err);
      setCompaniesError(message);
    } finally {
      setCompaniesLoading(false);
    }
  }, [selectedCompanyId]);

  const mapRowToContract = (row: any, empresaId: string): SubscriptionInfo => {
    const externalId = row?.external_subscription_id || '';
    const billingProvider = resolveBillingProvider(externalId);
    const paymentMethod = resolvePaymentMethod(externalId);
    const isManual = billingProvider === 'manual';
    return {
      id: row?.id || '',
      empresa_id: empresaId,
      externalId,
      status: row?.status || 'free',
      planType: row?.status === 'ativa' || row?.status === 'cancelamento_agendado' ? 'premium' : 'free',
      planName: row?.subscription_plan?.name || '',
      planId: row?.subscription_plan_id || '',
      startDate: row?.start_date || null,
      nextBillingDate: row?.next_billing_date || null,
      expiration: row?.end_date || null,
      lastChecked: new Date().toISOString(),
      billingProvider,
      isManual,
      paymentMethod,
    };
  };

  const loadCompanySubscription = useCallback(async (companyId: string) => {
    if (!companyId) return;
    setIsLoadingContext(true);
    setSubscriptionFeedback(null);
    setPaymentFeedback(null);
    try {
      const { data, error } = await supabase
        .from(APP_CONSTANTS.DATABASE.TABLES.SUBSCRIPTIONS.NAME)
        .select(
          'id, subscription_plan_id, empresa_id, start_date, end_date, status, next_billing_date, external_subscription_id, created_at, updated_at, subscription_plan:subscription_plan_id(id, name, description, price)',
        )
        .eq('empresa_id', companyId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        const contract = mapRowToContract(data, companyId);
        setSubscriptionInfo(contract);
        setSubscriptionPlanId(contract.planId || plans[0]?.id || '');
        setSubscriptionStatus(contract.status);
        setStartDate(contract.startDate ?? null);
        setNextBillingDate(contract.nextBillingDate ?? null);
        setEndDate(contract.expiration ?? null);
        if (contract.billingProvider === 'manual') {
          setBillingProvider('manual');
          setManualExternalId(contract.externalId || generateManualExternalId());
          setStripeExternalId('');
        } else if (contract.billingProvider === 'apple') {
          setBillingProvider('apple');
          setManualExternalId('');
          setStripeExternalId('');
        } else {
          setBillingProvider('stripe');
          setStripeExternalId(contract.externalId || '');
          setManualExternalId('');
        }
      } else {
        setSubscriptionInfo(null);
        setSubscriptionPlanId(plans[0]?.id || '');
        setSubscriptionStatus('ativa');
        setStartDate(null);
        setNextBillingDate(null);
        setEndDate(null);
        setBillingProvider('manual');
        setManualExternalId(generateManualExternalId());
        setStripeExternalId('');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar assinatura.';
      logger.error('[DEV-PANEL] Falha ao carregar assinatura da empresa', err);
      setSubscriptionInfo(null);
      setSubscriptionFeedback(`Erro ao carregar assinatura: ${message}`);
      setSubscriptionPlanId(plans[0]?.id || '');
      setSubscriptionStatus('ativa');
      setStartDate(null);
      setNextBillingDate(null);
      setEndDate(null);
      setBillingProvider('manual');
      setManualExternalId(generateManualExternalId());
      setStripeExternalId('');
    } finally {
      setIsLoadingContext(false);
    }
  }, [plans]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    if (!selectedCompanyId) return;
    const companyData = companies.find((c) => c.id === selectedCompanyId) || null;
    setSelectedCompany(companyData);
    if (plans.length === 0) return;
    loadCompanySubscription(selectedCompanyId);
  }, [selectedCompanyId, companies, plans, loadCompanySubscription]);

  const formattedCompanyName = useMemo(() => {
    if (!selectedCompany?.nome) return 'Selecione uma empresa';
    return selectedCompany.nome;
  }, [selectedCompany]);

  if (!isDeveloper) {
    return null;
  }

  const handleGenerateExternalId = () => {
    const newId = generateManualExternalId();
    setManualExternalId(newId);
    setBillingProvider('manual');
    setSubscriptionFeedback(`Novo identificador manual gerado: ${newId}`);
  };

  const ensureManualIdentifier = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) {
      return generateManualExternalId();
    }
    return trimmed.startsWith('manual:') ? trimmed : `manual:${trimmed.replace(/^manual:/, '')}`;
  };

  const handleSaveSubscription = async () => {
    if (!selectedCompanyId) {
      setSubscriptionFeedback('Selecione uma empresa para salvar a assinatura.');
      return;
    }

    if (!subscriptionPlanId) {
      setSubscriptionFeedback('Selecione um plano antes de salvar a assinatura.');
      return;
    }

    let externalIdToPersist = '';

    if (billingProvider === 'apple') {
      setSubscriptionFeedback('Assinaturas App Store devem ser atualizadas pelas funções Apple, não pelo painel manual.');
      return;
    }

    if (billingProvider === 'manual') {
      externalIdToPersist = ensureManualIdentifier(manualExternalId);
      setManualExternalId(externalIdToPersist);
    } else {
      const trimmed = stripeExternalId.trim();
      if (!trimmed) {
        setSubscriptionFeedback('Informe o ID da assinatura do Stripe para voltar ao fluxo automático.');
        return;
      }
      externalIdToPersist = trimmed;
    }

    setIsSavingSubscription(true);
    setSubscriptionFeedback(null);

    try {
      await upsertSubscriptionAdmin({
        empresaId: selectedCompanyId,
        subscriptionPlanId,
        status: subscriptionStatus,
        startDate: startDate || undefined,
        nextBillingDate: nextBillingDate || undefined,
        endDate: endDate || undefined,
        externalSubscriptionId: externalIdToPersist,
      });

      setSubscriptionFeedback('Assinatura atualizada com sucesso.');
      await loadCompanySubscription(selectedCompanyId);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro desconhecido ao salvar a assinatura.';
      logger.error('[DEV-PANEL] Falha ao salvar assinatura', error);
      setSubscriptionFeedback(`Erro: ${message}`);
    } finally {
      setIsSavingSubscription(false);
    }
  };

  const handleRegisterManualPayment = async () => {
    if (!selectedCompanyId) {
      setPaymentFeedback('Selecione uma empresa para registrar o pagamento.');
      return;
    }

    if (billingProvider !== 'manual') {
      setPaymentFeedback('Pagamentos manuais estão disponíveis apenas para assinaturas PIX.');
      return;
    }

    const targetExternalId = manualExternalId
      ? ensureManualIdentifier(manualExternalId)
      : subscriptionInfo?.externalId;

    if (!targetExternalId) {
      setPaymentFeedback('Gere ou informe o identificador manual antes de registrar o pagamento.');
      return;
    }

    const amountNumber = Number(paymentAmount.replace(',', '.'));
    if (Number.isNaN(amountNumber) || amountNumber <= 0) {
      setPaymentFeedback('Informe um valor válido para o pagamento.');
      return;
    }

    setIsRegisteringPayment(true);
    setPaymentFeedback(null);

    try {
      await registerManualPayment({
        empresaId: selectedCompanyId,
        subscriptionExternalId: targetExternalId,
        amount: amountNumber,
        paymentDate: paymentDate || undefined,
        status: paymentStatus,
        reference: paymentReference || undefined,
      });

      setPaymentFeedback('Pagamento manual registrado com sucesso.');
      setPaymentAmount('');
      setPaymentDate(null);
      setPaymentReference('');
      setPaymentStatus('paid');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro desconhecido ao registrar pagamento.';
      logger.error('[DEV-PANEL] Falha ao registrar pagamento manual', error);
      setPaymentFeedback(`Erro: ${message}`);
    } finally {
      setIsRegisteringPayment(false);
    }
  };

  const handleProviderChange = (provider: BillingProviderOption) => {
    setBillingProvider(provider);
    if (provider === 'manual' && !manualExternalId) {
      setManualExternalId(generateManualExternalId());
    }
    if (provider === 'stripe' && !stripeExternalId && subscriptionInfo?.billingProvider === 'stripe') {
      setStripeExternalId(subscriptionInfo.externalId);
    }
    if (provider === 'apple') {
      setSubscriptionFeedback('Assinatura App Store exibida apenas para auditoria. A alteração deve vir do fluxo Apple.');
    }
  };

  const formatMoney = (value?: number | null) => {
    if (value == null) return 'R$ -';
    return `R$ ${Number(value).toFixed(2).replace('.', ',')}`;
  };

  return (
    <View style={styles.container}>
      <View style={[styles.header, { paddingTop: headerPaddingTop }]}>
        <TouchableOpacity style={styles.backButton} onPress={() => router.back()}>
          <MaterialCommunityIcons name="arrow-left" size={24} color={theme.colors.text.inverse} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Painel do Desenvolvedor</Text>
        <View style={styles.headerRight} />
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[styles.scrollViewContent, { paddingBottom: scrollViewBottomPadding }]}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Ferramentas rápidas</Text>
          <TouchableOpacity
            style={styles.toolCard}
            onPress={() => router.push('/settings/developer/test-409')}
          >
            <View style={styles.toolCardRow}>
              <MaterialCommunityIcons
                name="shield-alert-outline"
                size={22}
                color={theme.colors.primary.default}
              />
              <Text style={styles.toolCardTitle}>Teste do upload 409</Text>
            </View>
            <Text style={styles.toolCardSubtitle}>
              Gera arquivos fictícios, registra pendências e executa o processPendingImages para inspecionar o tratamento de conflitos no Storage.
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.toolCard}
            onPress={() => router.push('/settings/developer/test-log-buffer')}
          >
            <View style={styles.toolCardRow}>
              <MaterialCommunityIcons
                name="file-document-edit-outline"
                size={22}
                color={theme.colors.primary.default}
              />
              <Text style={styles.toolCardTitle}>Teste buffer de logs</Text>
            </View>
            <Text style={styles.toolCardSubtitle}>
              Simula a sequência que salva um caminho local em campos de checklist e dispara o buffer circular para checklist_logs.
            </Text>
          </TouchableOpacity>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Empresas</Text>
          {companiesLoading ? (
            <View style={styles.loadingRow}>
              <ActivityIndicator size="small" color={theme.colors.primary.default} />
              <Text style={styles.loadingText}>Carregando empresas...</Text>
            </View>
          ) : companiesError ? (
            <Text style={styles.errorText}>{companiesError}</Text>
          ) : companies.length === 0 ? (
            <Text style={styles.helperText}>Nenhuma empresa encontrada para gerenciamento.</Text>
          ) : (
            <View style={styles.companyList}>
              {companies.map((company) => (
                <CompanyCard
                  key={company.id}
                  company={company}
                  selected={selectedCompanyId === company.id}
                  onPress={() => setSelectedCompanyId(company.id)}
                />
              ))}
            </View>
          )}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Contexto da empresa selecionada</Text>
          <View style={styles.infoCard}>
            <MaterialCommunityIcons name="office-building-outline" size={22} color={theme.colors.text.secondary} />
            <View style={styles.infoCardTextWrapper}>
              <Text style={styles.infoCardTitle}>{formattedCompanyName}</Text>
              <Text style={styles.infoCardText}>
                Plano atual: {selectedCompany?.plano ? selectedCompany.plano.toUpperCase() : 'não configurado'}.
              </Text>
              {subscriptionInfo ? (
                <Text style={styles.infoCardText}>
                  Assinatura: {subscriptionInfo.status.toUpperCase()} • {getBillingProviderLabel(subscriptionInfo.billingProvider)} •{' '}
                  {subscriptionInfo.externalId || 'sem identificador'}
                </Text>
              ) : (
                <Text style={styles.infoCardInfoMuted}>Empresa ainda não possui assinatura registrada.</Text>
              )}
              {subscriptionInfo?.nextBillingDate && (
                <Text style={styles.infoCardText}>
                  Próxima cobrança: {formatDateForDisplay(subscriptionInfo.nextBillingDate)}
                </Text>
              )}
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Assinatura</Text>

          <Text style={styles.label}>Provedor de cobrança</Text>
          <View style={styles.statusRow}>
            {(['manual', 'stripe', 'apple'] as BillingProviderOption[]).map((provider) => (
              <TouchableOpacity
                key={provider}
                style={[
                  styles.statusChip,
                  billingProvider === provider && styles.statusChipSelected,
                  provider === 'apple' && styles.statusChipDisabled,
                ]}
                onPress={() => handleProviderChange(provider)}
                disabled={provider === 'apple'}
              >
                <Text
                  style={[
                    styles.statusChipText,
                    billingProvider === provider && styles.statusChipTextSelected,
                  ]}
                >
                  {provider === 'manual' ? 'PIX manual' : provider === 'apple' ? 'App Store' : 'Stripe automático'}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {billingProvider === 'manual' ? (
            <>
              <Text style={styles.label}>Identificador manual (external_subscription_id)</Text>
              <View style={styles.inlineField}>
                <TextInput
                  style={styles.input}
                  value={manualExternalId}
                  onChangeText={setManualExternalId}
                  placeholder="manual:..."
                  autoCapitalize="none"
                />
                <TouchableOpacity style={styles.inlineButton} onPress={handleGenerateExternalId}>
                  <MaterialCommunityIcons name="refresh" size={20} color={theme.colors.text.inverse} />
                  <Text style={styles.inlineButtonText}>Gerar</Text>
                </TouchableOpacity>
              </View>
              <Text style={styles.helperText}>Se vazio, um identificador será gerado automaticamente ao salvar.</Text>
            </>
          ) : billingProvider === 'apple' ? (
            <>
              <Text style={styles.label}>Identificador Apple (external_subscription_id)</Text>
              <TextInput
                style={[styles.input, styles.inputDisabled]}
                value={subscriptionInfo?.externalId || ''}
                editable={false}
                placeholder="manual:apple:..."
                autoCapitalize="none"
              />
              <Text style={styles.helperText}>
                Este registro e os pagamentos App Store devem ser mantidos pelas Edge Functions Apple.
              </Text>
            </>
          ) : (
            <>
              <Text style={styles.label}>ID da assinatura do Stripe (external_subscription_id)</Text>
              <TextInput
                style={styles.input}
                value={stripeExternalId}
                onChangeText={setStripeExternalId}
                placeholder="sub_xxx..."
                autoCapitalize="none"
              />
              <Text style={styles.helperText}>
                Informe o ID da assinatura ativa no Stripe para retomar o fluxo automático de cobrança.
              </Text>
            </>
          )}

          <Text style={[styles.label, styles.planLabel]}>
            Plano disponível
          </Text>
          {isLoadingPlans ? (
            <View style={styles.loadingRow}>
              <ActivityIndicator size="small" color={theme.colors.primary.default} />
              <Text style={styles.loadingText}>Carregando planos...</Text>
            </View>
          ) : plansError ? (
            <Text style={styles.errorText}>{plansError}</Text>
          ) : (
            <View>
              {plans.map((plan) => {
                const isSelected = subscriptionPlanId === plan.id;
                return (
                  <TouchableOpacity
                    key={plan.id}
                    style={[styles.planOption, isSelected && styles.planOptionSelected]}
                    onPress={() => setSubscriptionPlanId(plan.id)}
                  >
                    <View style={styles.planOptionHeader}>
                      <Text style={styles.planOptionTitle}>{plan.name}</Text>
                      {isSelected && (
                        <MaterialCommunityIcons
                          name="check-circle"
                          size={18}
                          color={theme.colors.util.success}
                        />
                      )}
                    </View>
                    <Text style={styles.planOptionPrice}>{formatMoney(plan.price)}</Text>
                    {plan.description && (
                      <Text style={styles.planOptionDescription}>{plan.description}</Text>
                    )}
                  </TouchableOpacity>
                );
              })}
            </View>
          )}

          <Text style={styles.label}>Status da assinatura</Text>
          <View style={styles.statusRow}>
            {(['ativa', 'cancelamento_agendado', 'cancelada', 'free'] as const).map((status) => (
              <TouchableOpacity
                key={status}
                style={[
                  styles.statusChip,
                  subscriptionStatus === status && styles.statusChipSelected,
                ]}
                onPress={() => setSubscriptionStatus(status)}
              >
                <Text
                  style={[
                    styles.statusChipText,
                    subscriptionStatus === status && styles.statusChipTextSelected,
                  ]}
                >
                  {status}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <DateSelector
            label="Início da assinatura"
            value={startDate}
            onChange={(val) => setStartDate(val)}
          />
          <DateSelector
            label="Próxima cobrança"
            value={nextBillingDate}
            onChange={(val) => setNextBillingDate(val)}
          />
          <DateSelector
            label="Data de encerramento (opcional)"
            value={endDate}
            onChange={(val) => setEndDate(val)}
            allowClear
          />

          {subscriptionFeedback && (
            <Text
              style={[
                styles.feedbackText,
                subscriptionFeedback.startsWith('Erro') && styles.feedbackError,
              ]}
            >
              {subscriptionFeedback}
            </Text>
          )}

          <TouchableOpacity
            style={[styles.primaryButton, (isSavingSubscription || isLoadingContext || billingProvider === 'apple') && styles.disabledButton]}
            onPress={handleSaveSubscription}
            disabled={isSavingSubscription || isLoadingContext || billingProvider === 'apple'}
          >
            {isSavingSubscription ? (
              <ActivityIndicator size="small" color={theme.colors.text.inverse} />
            ) : (
              <Text style={styles.primaryButtonText}>Salvar assinatura</Text>
            )}
          </TouchableOpacity>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Registrar pagamento PIX manual</Text>
          {billingProvider !== 'manual' && (
            <Text style={styles.helperText}>
              Altere o provedor para "PIX manual" para habilitar o registro de pagamentos manuais.
            </Text>
          )}

          <Text style={styles.label}>Valor pago</Text>
          <TextInput
            style={styles.input}
            value={paymentAmount}
            onChangeText={setPaymentAmount}
            placeholder="Ex: 149.90"
            keyboardType="decimal-pad"
          />

          <DateSelector
            label="Data do pagamento (opcional)"
            value={paymentDate}
            onChange={(val) => setPaymentDate(val)}
            allowClear
          />

          <Text style={styles.label}>Status do pagamento</Text>
          <View style={styles.statusRow}>
            {(['paid', 'pending', 'failed'] as const).map((status) => (
              <TouchableOpacity
                key={status}
                style={[
                  styles.statusChip,
                  paymentStatus === status && styles.statusChipSelected,
                  billingProvider !== 'manual' && styles.statusChipDisabled,
                ]}
                onPress={() => billingProvider === 'manual' && setPaymentStatus(status)}
                disabled={billingProvider !== 'manual'}
              >
                <Text
                  style={[
                    styles.statusChipText,
                    paymentStatus === status && styles.statusChipTextSelected,
                  ]}
                >
                  {status}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <Text style={styles.label}>Referência (opcional)</Text>
          <TextInput
            style={styles.input}
            value={paymentReference}
            onChangeText={setPaymentReference}
            placeholder="Ex: PIX-123456"
            autoCapitalize="characters"
          />

          {paymentFeedback && (
            <Text
              style={[
                styles.feedbackText,
                paymentFeedback.startsWith('Erro') && styles.feedbackError,
              ]}
            >
              {paymentFeedback}
            </Text>
          )}

          <TouchableOpacity
            style={[
              styles.secondaryButton,
              (billingProvider !== 'manual' || isRegisteringPayment) && styles.disabledButton,
            ]}
            onPress={handleRegisterManualPayment}
            disabled={billingProvider !== 'manual' || isRegisteringPayment}
          >
            {isRegisteringPayment ? (
              <ActivityIndicator size="small" color={theme.colors.primary.default} />
            ) : (
              <Text style={styles.secondaryButtonText}>Registrar pagamento manual</Text>
            )}
          </TouchableOpacity>
        </View>
      </ScrollView>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
  },
  header: {
    backgroundColor: theme.colors.primary.default,
    padding: theme.spacing.lg,
    paddingTop: Platform.OS === 'ios' ? theme.spacing.xl + theme.spacing.sm : theme.spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: theme.fontSizes.xl,
    fontWeight: theme.fontWeights.bold as any,
    color: theme.colors.text.inverse,
  },
  headerRight: {
    width: 40,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    padding: theme.spacing.lg,
    paddingBottom: theme.spacing.xl,
  },
  section: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.lg,
    ...theme.shadows.sm,
    marginBottom: theme.spacing.lg,
  },
  sectionTitle: {
    fontSize: theme.fontSizes.lg,
    fontWeight: theme.fontWeights.semibold as any,
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.md,
  },
  toolCard: {
    borderWidth: 1,
    borderColor: theme.colors.primary.light,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    backgroundColor: 'rgba(44,127,255,0.08)',
    gap: theme.spacing.xs,
  },
  toolCardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  toolCardTitle: {
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.medium as any,
    color: theme.colors.text.primary,
  },
  toolCardSubtitle: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.secondary,
  },
  companyList: {
    marginTop: theme.spacing.sm,
  },
  companyCard: {
    borderWidth: 1,
    borderColor: theme.colors.border.default,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.background.secondary,
  },
  companyCardSelected: {
    borderColor: theme.colors.primary.default,
    backgroundColor: 'rgba(44, 127, 255, 0.08)',
  },
  companyCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.xs,
  },
  companyCardTitle: {
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.semibold as any,
    color: theme.colors.text.primary,
  },
  companyCardSubtitle: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
  },
  companyCardInfo: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.secondary,
    marginTop: theme.spacing.xs,
  },
  companyCardInfoMuted: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.tertiary,
    marginTop: theme.spacing.xs,
  },
  infoCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    backgroundColor: 'rgba(250, 250, 250, 0.6)',
  },
  infoCardTextWrapper: {
    flex: 1,
    marginLeft: theme.spacing.sm,
  },
  infoCardTitle: {
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.semibold as any,
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.xs,
  },
  infoCardText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
    marginBottom: theme.spacing.xs,
  },
  infoCardInfoMuted: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.tertiary,
    marginBottom: theme.spacing.xs,
  },
  label: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
    marginTop: theme.spacing.md,
    marginBottom: theme.spacing.xs,
  },
  planLabel: {
    marginTop: theme.spacing.lg,
  },
  input: {
    borderWidth: 1,
    borderColor: theme.colors.border.default,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.primary,
    backgroundColor: theme.colors.background.secondary,
  },
  inputDisabled: {
    color: theme.colors.text.secondary,
    backgroundColor: theme.colors.background.tertiary,
  },
  helperText: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.tertiary,
    marginTop: theme.spacing.xs,
  },
  inlineField: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: theme.spacing.xs,
  },
  inlineButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.primary.default,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    marginLeft: theme.spacing.sm,
  },
  inlineButtonText: {
    color: theme.colors.text.inverse,
    fontWeight: theme.fontWeights.semibold as any,
    marginLeft: theme.spacing.xs,
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: theme.spacing.sm,
  },
  loadingText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
    marginLeft: theme.spacing.sm,
  },
  errorText: {
    color: theme.colors.util.error,
    fontSize: theme.fontSizes.sm,
  },
  planOption: {
    borderWidth: 1,
    borderColor: theme.colors.border.default,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.background.secondary,
    marginBottom: theme.spacing.sm,
  },
  planOptionSelected: {
    borderColor: theme.colors.primary.default,
    backgroundColor: 'rgba(44, 127, 255, 0.08)',
  },
  planOptionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.xs,
  },
  planOptionTitle: {
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.semibold as any,
    color: theme.colors.text.primary,
  },
  planOptionPrice: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
    marginBottom: theme.spacing.xs,
  },
  planOptionDescription: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.tertiary,
    lineHeight: 18,
  },
  statusRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: theme.spacing.sm,
  },
  statusChip: {
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.xs,
    borderRadius: theme.borderRadius.circular,
    borderWidth: 1,
    borderColor: theme.colors.border.default,
    marginRight: theme.spacing.sm,
    marginBottom: theme.spacing.sm,
  },
  statusChipSelected: {
    borderColor: theme.colors.primary.default,
    backgroundColor: 'rgba(44, 127, 255, 0.1)',
  },
  statusChipDisabled: {
    opacity: 0.5,
  },
  statusChipText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
  },
  statusChipTextSelected: {
    color: theme.colors.primary.default,
    fontWeight: theme.fontWeights.semibold as any,
  },
  feedbackText: {
    marginTop: theme.spacing.sm,
    fontSize: theme.fontSizes.sm,
    color: theme.colors.util.success,
  },
  feedbackError: {
    color: theme.colors.util.error,
  },
  primaryButton: {
    marginTop: theme.spacing.lg,
    backgroundColor: theme.colors.primary.default,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    alignItems: 'center',
  },
  primaryButtonText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.semibold as any,
  },
  secondaryButton: {
    marginTop: theme.spacing.lg,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: theme.colors.primary.default,
    backgroundColor: theme.colors.background.primary,
  },
  secondaryButtonText: {
    color: theme.colors.primary.default,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.semibold as any,
  },
  disabledButton: {
    opacity: 0.5,
  },
  dateField: {
    marginTop: theme.spacing.md,
    position: 'relative',
    zIndex: 5,
  },
  dateButton: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dateButtonText: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.primary,
  },
  clearButton: {
    marginTop: theme.spacing.sm,
    flexDirection: 'row',
    alignItems: 'center',
  },
  clearButtonInline: {
    marginTop: theme.spacing.xs,
    flexDirection: 'row',
    alignItems: 'center',
  },
  clearButtonText: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.secondary,
    marginLeft: theme.spacing.xs,
  },
  iosModalOverlay: {
    flex: 1,
    backgroundColor: theme.colors.overlay.semi,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iosModalContent: {
    width: '85%',
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
  },
  iosModalButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: theme.spacing.md,
  },
  cancelButton: {
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    marginRight: theme.spacing.sm,
  },
  cancelButtonText: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.md,
  },
  confirmButton: {
    backgroundColor: theme.colors.primary.default,
    borderRadius: theme.borderRadius.md,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
  },
  confirmButtonText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.semibold as any,
  },
});

export default DeveloperPanelScreen;
